import { getMedias } from "@omni/mappers/product/shared/media.mapper";
import { CartItem } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { Button } from "@omni/ui/components/Button/button.component";
import * as CartUtils from "@omni/utils/cart.utils";
import { cn } from "@omni/utils/styles";
import { FC, useMemo, useRef } from "react";

import { ConfigurableProductImageComponent } from "@/components/Cart/ShoppingCart/productCollectionBlock/configurableProductImage/configurableProductImage.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { ShoppingCartSummaryRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductImageContainer } from "@/components/Product/ProductImage/productImage.container";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { ConditionalWrapper } from "@/utils/helpers";
import { useSwipeAction } from "@/utils/hooks/useSwipeAction.hook";
import { formatPrice } from "@/utils/price.utils";
import { getProductConfigurationPreview } from "@/utils/productConfiguration";
import {
    getProductConfiguration,
    productConfigurations
} from "@/utils/productConfiguration/productConfiguration.utils";

type ShoppingCartItemProps = {
    cartItem: CartItem;
    productLinkingAvailable: boolean;
    loading: boolean;
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
};

export const ShoppingCartItem: FC<ShoppingCartItemProps> = ({
    cartItem,
    productLinkingAvailable,
    loading,
    removeFromCart
}: ShoppingCartItemProps) => {
    const { t } = useGranite();

    const productConfiguration = useMemo(
        () => getProductConfiguration({ sku: cartItem.sku }),
        [cartItem.sku]
    );

    const removeArticle = (id: string, quantity: number, free: boolean) => {
        if (removeFromCart) {
            removeFromCart(id, quantity, free);
        }
    };

    const withProductLink = (cartItem: CartItem): boolean =>
        productLinkingAvailable &&
        !cartItem.warrantyDeposit &&
        !cartItem.articleConfiguration;

    const elementRef = useRef<HTMLDivElement | null>(null);

    const { tresholdReached, handlers } = useSwipeAction({
        elementRef: elementRef,
        action: () => {
            removeArticle(
                cartItem.id!,
                cartItem.quantity,
                cartItem.freeEntryAddedByCustomer
            );
        }
    });

    return (
        <div className={cn("tw:h-25 tw:relative tw:overflow-hidden")}>
            <div
                className={cn(
                    "tw:absolute tw:w-full tw:h-full tw:flex tw:justify-end tw:items-center tw:transition-opacity",
                    {
                        "tw:bg-danger": tresholdReached,
                        "tw:bg-danger tw:opacity-50": !tresholdReached
                    }
                )}
            >
                <div
                    className={cn("tw:text-white tw:block tw:m-4 tw:scale-150")}
                >
                    <i className={cn("las la-trash la-lg")} />
                </div>
            </div>
            <Row
                {...(!loading && removeFromCart && handlers)}
                ref={elementRef}
                className={cn(
                    "tw:absolute tw:w-full tw:h-full tw:overflow-hidden tw:bg-background tw:shadow-md tw:transition-transform tw:text-sm tw:border-b tw:border-grey-light tw:font-light tw:m-0 tw:group",
                    {
                        "tw:after:content-[''] tw:after:animate-loading-bar tw:after:block tw:after:h-full tw:after:w-4 tw:after:absolute tw:after:top-0 tw:after:left-0 tw:after:p-0 tw:after:opacity-30 tw:after:bg-grey-light":
                            loading
                    }
                )}
            >
                <Col
                    xs={3}
                    className={cn("tw:h-full tw:pr-4 tw:flex tw:items-center")}
                >
                    {!cartItem.warrantyDeposit &&
                        !cartItem.articleConfiguration && (
                            <ConditionalWrapper
                                condition={withProductLink(cartItem)}
                                wrapper={(children) => (
                                    <a
                                        href={CartUtils.getUrl(
                                            cartItem,
                                            productConfigurations,
                                            Config.pages.giftcard
                                        )}
                                        title={CartUtils.getTitle(cartItem, t)}
                                    >
                                        {children}
                                    </a>
                                )}
                            >
                                <ProductImageContainer
                                    title={CartUtils.getTitle(cartItem, t)}
                                    medias={getMedias(
                                        cartItem.warrantyDeposit,
                                        CartUtils.getTitle(cartItem, t),
                                        cartItem.product?.mainImages,
                                        cartItem.articleConfiguration
                                    )}
                                    isEcoChequesAccepted={false}
                                    renditions={ShoppingCartSummaryRenditions}
                                    className={cn("tw:h-full")}
                                    parentFit={true}
                                />
                            </ConditionalWrapper>
                        )}
                    {cartItem.articleConfiguration && (
                        <ConfigurableProductImageComponent
                            title={CartUtils.getTitle(cartItem, t)}
                            imageUrl={
                                cartItem.articleConfiguration.imageURL ??
                                undefined
                            }
                        />
                    )}
                </Col>
                <Col
                    xs={5}
                    className={cn("tw:h-full tw:flex tw:items-center")}
                >
                    {withProductLink(cartItem) && (
                        <a
                            href={CartUtils.getUrl(
                                cartItem,
                                productConfigurations,
                                Config.pages.giftcard
                            )}
                            className={cn("clamp-3", "tw:text-grey-dark")}
                        >
                            {CartUtils.getTitle(cartItem, t)}{" "}
                            {getProductConfigurationPreview(
                                cartItem,
                                productConfiguration
                            )}
                        </a>
                    )}
                    {!withProductLink(cartItem) && (
                        <div className={cn("clamp-3")}>
                            {CartUtils.getTitle(cartItem, t)}{" "}
                            {getProductConfigurationPreview(
                                cartItem,
                                productConfiguration
                            )}
                        </div>
                    )}
                </Col>
                <Col
                    xs={1}
                    className={cn(
                        "tw:h-full tw:flex tw:items-center tw:justify-center"
                    )}
                >
                    {cartItem.quantity}
                </Col>
                <Col
                    xs={3}
                    className={cn("tw:h-full tw:text-right tw:font-bold")}
                >
                    <div
                        className={cn(
                            "tw:h-full tw:flex tw:justify-end tw:content-center tw:w-full"
                        )}
                    >
                        <div className={cn("tw:self-center")}>
                            {CartUtils.showStrippedPrice(cartItem) && (
                                <s className={cn("tw:font-light tw:block")}>
                                    {formatPrice(
                                        cartItem.articleStrikethroughPrice ??
                                            undefined
                                    )}
                                </s>
                            )}
                            <span className={cn("tw:block")}>
                                {formatPrice(
                                    cartItem.articleSalesPrice,
                                    cartItem.free
                                        ? { freeLabel: true, t: t }
                                        : undefined
                                )}
                            </span>
                        </div>
                        {!loading &&
                            removeFromCart &&
                            !cartItem.warrantyDeposit && (
                                <div
                                    className={cn(
                                        "tw:self-center tw:max-w-0 tw:overflow-hidden tw:transition-all",
                                        "tw:group-hover:max-w-full"
                                    )}
                                >
                                    {(!cartItem.free ||
                                        cartItem.freeEntryAddedByCustomer) && (
                                        <Button
                                            color={"link"}
                                            type={"button"}
                                            size={"sm"}
                                            className={cn("tw:p-0 tw:pl-2")}
                                            test={`shoppingCartSummary-${cartItem.sku}-button-delete`}
                                            onClick={() => {
                                                removeArticle(
                                                    cartItem.id!,
                                                    cartItem.quantity,
                                                    cartItem.freeEntryAddedByCustomer
                                                );
                                            }}
                                            aria-label={t(
                                                "accessibility.shoppingCartSummary.remove"
                                            )}
                                        >
                                            <i
                                                className={cn(
                                                    "las la-trash la-lg"
                                                )}
                                            />
                                        </Button>
                                    )}
                                </div>
                            )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};
